import ApplicationController from "../application_component_controller";
import URL from "hb/lib/url";
import { useOutlets } from "hb/insights/use_outlets";
import hljs from "highlight.js/lib/core";

export default class extends ApplicationController {
  static targets = ["expressionGroup"];

  connect() {
    useOutlets(this, { components: ["panels", "tab_rail"] });

    this.element.querySelectorAll("pre > code").forEach((ele) => {
      hljs.highlightElement(ele);
    });

    this.docLinkHandler = this.handleDocLink.bind(this);
    this.docCloseHandler = this.handleDocClose.bind(this);

    window.addEventListener("doclink", this.docLinkHandler);
    window.addEventListener("closedocs", this.docCloseHandler);

    this.openDocId(URL.getParam("doc"), false);
  }

  disconnect() {
    window.removeEventListener("doclink", this.docLinkHandler);
    window.removeEventListener("closedocs", this.docCloseHandler);
  }

  handleExpressionFn({ params: { fn } }) {
    this.selectExpressionFn(fn);
  }

  handleDocLink(evt) {
    this.openDocId(evt?.detail?.link || evt?.params?.link, evt?.detail?.smooth);
  }

  openDocId(id, smooth = true) {
    if (id) {
      this.ensureDocsOpen();
      this.gotoLink(id, smooth);
    }
  }

  ensureDocsOpen() {
    // Tab rail (queries page): ask the rail to select the docs tab AND open
    // the panel. Toggling only the panel column would leave the rail's
    // active tab unchanged, so the docs body could still be hidden behind
    // another tab.
    const rail = this.outletComponent("tab_rail");
    if (rail) {
      rail.openDocs();
      return;
    }

    // PanelsComponent without a tab rail: open the end panel directly.
    const panels = this.outletComponent("panels");
    if (panels) {
      const el = panels.element;
      const closed =
        el.classList.contains("end-panel-snapped") ||
        el.classList.contains("end-panel-hidden");
      if (closed) panels.toggleEndPanel();
      return;
    }

    // Legacy alarm-wrap path.
    const wrap = document.querySelector(".search-wrap, .alarm-wrap");
    if (!wrap) return;
    wrap.classList.remove("container");
    wrap.classList.add("with-docs");
  }

  handleDocClose() {
    const rail = this.outletComponent("tab_rail");
    if (rail) {
      rail.closePanel();
      return;
    }

    const panels = this.outletComponent("panels");
    if (panels) {
      const el = panels.element;
      const open =
        !el.classList.contains("end-panel-snapped") &&
        !el.classList.contains("end-panel-hidden");
      if (open) panels.toggleEndPanel();
      return;
    }

    const wrap = document.querySelector(".search-wrap, .alarm-wrap");
    if (!wrap) return;
    wrap.classList.add("container");
    wrap.classList.remove("with-docs");
  }

  gotoLink(id, smooth) {
    const [, fn] = id.split("expr-");
    if (fn) {
      this.gotoExpressionFn(fn, smooth);
    } else {
      this.gotoHeading(id, smooth);
    }
  }

  gotoHeading(id, smooth) {
    const sel = document.getElementById(id);
    if (sel === null) {
      return;
    }
    let offset = 0;
    const block = sel.closest(".block");
    if (block) {
      block.classList.add("expanded");

      if (sel === block) {
        offset = 40;
      } else {
        offset = 90;
      }
    }
    this.element.scrollTo({
      top: sel.offsetTop - offset,
      behavior: smooth ? "smooth" : "instant",
    });
  }

  gotoExpressionFn(id, smooth) {
    const block = this.selectExpressionFn(id);
    block.classList.add("expanded");
    this.element.scrollTo({
      top: block.offsetTop - 40,
      behavior: smooth ? "smooth" : "instant",
    });
  }

  selectExpressionFn(fn) {
    const sel = document.getElementById(`expSel-${fn}`);
    const desc = document.getElementById(`expDesc-${fn}`);
    if (sel === null || desc === null) {
      return;
    }
    const block = sel.closest(".block");
    block
      .querySelectorAll(`:scope .selected`)
      .forEach((e) => e.classList.remove("selected"));
    sel.classList.add("selected");
    desc.classList.add("selected");

    return block;
  }
}
